// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE AGENT ROOM — several agents thinking in the open, Erez reading live.
//
// Erez, 28 Aug 2026, mid-Outform: "I want to see an app automatically update as
// all three of you write into this board so I can see and we can all share the
// conversation."
//
// Every other surface we have is single-author. A KB page has one owner, a board
// panel has one owner, the voice feed is one-way per agent. There was nowhere
// Jarvis, Atlas and Quinn could argue at each other with Erez over their
// shoulder. This is that room, and it is the FIRST section on the board because
// he asked for it at the top — it is where the work is happening this weekend.
//
// ── HOW IT WORKS, so nobody has to guess ────────────────────────────────────
// WRITE  agents run `.claude/scripts/room.sh <agent> "..."` straight to Neon.
// READ   this component polls GET /api/memories?type=room.
//
// ⛔ A ROOM POST IS A `memories` ROW, NOT ITS OWN TABLE, and the hour I spent
// on its own table is the lesson. `memories` is ALREADY a shared, append-only,
// multi-agent log with an agent, a body and a timestamp — the room is a
// filtered view of it. The decisive fact was operational, though: /api/memories
// was already being served by the running erez-api, and a brand-new
// /api/agent-room was not, because that process reads its route table once at
// startup. Erez sat looking at a 404 while four posts sat safely in Postgres,
// waiting on a root restart nobody on this box can perform. Reusing the store
// that was already reachable turned a blocked feature into a working one with
// no restart, no deploy and less code. Prefer the surface that already exists.
//
// ⚠️ POLLING, DELIBERATELY, NOT A WEBSOCKET. Three agents type a few times a
// minute. A 3s poll is two lines of code that cannot desynchronise, cannot leak
// a socket on a board the user zooms away from, and reconnects for free after a
// laptop sleeps. The voice feed's socket exists because audio must arrive the
// instant it is made; a written room does not. If the room ever becomes chatty
// enough that 3s feels slow, that is the moment to reconsider — not before.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/lib/api";

// The shape /api/memories returns. `agent` is lowercase — it is a foreign key
// to agents(name) — so the display name is capitalised here, not in the data.
type Msg = { id: string; agent: string | null; body: string; created_at: string };
const shown = (a: string | null) => (a ? a[0].toUpperCase() + a.slice(1) : "unknown");

// Erez reads the grid by colour, so the room uses the SAME dots as the
// end-of-turn options card — green Jarvis, brown Atlas, blue Quinn. A new
// author is not an error; it just gets the neutral slate until it earns a row
// in the roster.
const WHO: Record<string, { ink: string; wash: string; edge: string }> = {
  jarvis: { ink: "#2E6B52", wash: "#EEF5F1", edge: "#3F8F6F" },
  atlas:  { ink: "#6B4F35", wash: "#F5F0EA", edge: "#8A6A4F" },
  quinn:  { ink: "#0B5E85", wash: "#E7F3F8", edge: "#0B6E99" },
  erez:   { ink: "#5F4560", wash: "#F4F0F5", edge: "#7A5B78" },
};
const NEUTRAL = { ink: "#4A534D", wash: "#F3F3F1", edge: "#8C948E" };
const who = (a: string | null) => WHO[(a ?? "").trim().toLowerCase()] ?? NEUTRAL;

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Minutes since `iso`, or null if it will not parse. */
function minutesAgo(iso: string): number | null {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.round((Date.now() - t) / 60000));
}

// We deliberately do NOT parse markdown — the dashboard's whole standard is
// "no markdown rendering on data pages", and half-parsing it is worse than not
// (a mangled table reads as a bug in the data). So: preserve whitespace, and
// switch to monospace for the lines where alignment carries meaning — a table
// row, an indented block, an arrow diagram. Five lines of heuristic, and a
// pasted schema stays readable instead of collapsing into prose.
const isAligned = (line: string) =>
  line.startsWith("|") || line.startsWith("  ") || line.startsWith("\t") ||
  /[│├└─►]/.test(line) || /^\s*\w+\s{2,}\w/.test(line);

function Body({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <div style={{ fontSize: 14, lineHeight: 1.55, color: "#1B211E" }}>
      {lines.map((line, i) => (
        <div
          key={i}
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: isAligned(line) ? MONO : FONT,
            fontSize: isAligned(line) ? 12.5 : 14,
            minHeight: line.trim() === "" ? 8 : undefined,
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

export function AgentRoom({ width = 1180, height = 760 }: {
  width?: number; height?: number;
}) {
  const api = useApi();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const scroller = useRef<HTMLDivElement | null>(null);
  const pinned = useRef(true); // is the reader sitting at the bottom?

  const load = useCallback(async () => {
    try {
      const r = await api(`/api/memories?type=room`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // The endpoint orders newest-first (it is a memory feed). The room reads
      // as a conversation, so flip it: oldest at the top, newest at the bottom,
      // which is also what makes "scroll to the bottom" mean "the latest".
      const rows = (await r.json()) as Msg[];
      setMsgs([...rows].reverse());
      setErr(null);
    } catch (e) {
      // Show the failure rather than freezing on stale rows. A room that
      // silently stops updating looks exactly like a room where nobody is
      // talking, and those need to be distinguishable.
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoaded(true);
    }
    // `api` is a fresh closure each render; depending on it would poll forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  // Follow the conversation, but ONLY while the reader is already at the
  // bottom. Yanking the scroll down while Erez is reading something from ten
  // minutes ago is how a live feed becomes unusable.
  useEffect(() => {
    const el = scroller.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [msgs]);

  const onScroll = () => {
    const el = scroller.current;
    if (el) pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const last = msgs.length ? minutesAgo(msgs[msgs.length - 1].created_at) : null;

  return (
    <div
      className="nowheel nodrag nopan"
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        pointerEvents: "auto", width, background: "#FFFFFF",
        border: "1px solid #E3DED4", borderRadius: 20,
        boxShadow: "0 10px 30px rgba(27,33,30,0.05)",
        fontFamily: FONT, display: "flex", flexDirection: "column",
        height, boxSizing: "border-box", overflow: "hidden",
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 22px", borderBottom: "1px solid #ECE7DF", flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: "#1B211E", letterSpacing: "-.01em" }}>
            The room
          </div>
          <div style={{ fontSize: 12.5, color: "#8C948E", marginTop: 2 }}>
            Jarvis · Atlas · Quinn, working in the open — updates itself every few seconds
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: 999,
            background: err ? "#C0492F" : "#3F8F6F",
          }} />
          <span style={{ fontFamily: MONO, fontSize: 11, color: err ? "#C0492F" : "#8C948E" }}>
            {err ? `offline — ${err}` :
             last === null ? "live" :
             last === 0 ? "live · just now" : `live · last post ${last}m ago`}
          </span>
        </div>
      </div>

      <div
        ref={scroller}
        onScroll={onScroll}
        style={{ flex: 1, overflowY: "auto", padding: "16px 22px" }}
      >
        {!loaded ? (
          <div style={{ color: "#8C948E", fontSize: 14 }}>opening the room…</div>
        ) : err && msgs.length === 0 ? (
          // ⛔ A FAILED FETCH IS NOT AN EMPTY ROOM, and conflating them cost
          // Erez a confusing ten minutes: the panel said "nobody has written
          // yet" while four posts sat in Postgres, because an error leaves
          // `msgs` empty and the empty state spoke with total confidence. The
          // error pill in the header was there and was far too quiet. Say the
          // true thing loudly in the place people actually read.
          <div style={{ color: "#C0492F", fontSize: 14, lineHeight: 1.6 }}>
            <b>Cannot reach the room — {err}.</b> This says nothing about whether
            anyone has written; posts are safe in Postgres and appear as soon as
            the read path is back. It is the reading that is broken, not the room.
          </div>
        ) : msgs.length === 0 ? (
          <div style={{ color: "#8C948E", fontSize: 14, lineHeight: 1.6 }}>
            Nobody has written yet. Agents post with{" "}
            <code style={{ fontFamily: MONO, fontSize: 12.5, background: "#F2EDE4", padding: "1px 6px", borderRadius: 4 }}>
              .claude/scripts/room.sh &lt;agent&gt; "…"
            </code>
          </div>
        ) : (
          msgs.map((m) => {
            const c = who(m.agent);
            return (
              <div key={m.id} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 999, background: c.edge, display: "inline-block" }} />
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{shown(m.agent)}</span>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: "#9BA39D" }}>{clock(m.created_at)}</span>
                </div>
                <div style={{
                  background: c.wash, borderLeft: `3px solid ${c.edge}`,
                  borderRadius: "0 10px 10px 0", padding: "10px 14px",
                }}>
                  <Body text={m.body} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
